import { useEffect, useRef } from 'react';

export type EventType = 
  | 'sector-changed'
  | 'demand-status-changed'
  | 'demand-assigned'
  | 'demand-assignment-removed'
  | 'demand-created'
  | 'demand-updated'
  | 'demand-deleted'
  | 'user-updated'
  | 'cache-invalidated';

export interface EventData {
  'sector-changed': { 
    oldSectorId?: number; 
    newSectorId: number; 
    user: any;
  };
  'demand-status-changed': { 
    demandId: number; 
    oldStatus: string; 
    newStatus: string; 
    demanda?: any;
  };
  'demand-assigned': { 
    demandId: number; 
    assignedTo: number[]; 
    demanda?: any;
  };
  'demand-assignment-removed': { 
    demandId: number; 
    userId: number;
  };
  'demand-created': { 
    demanda: any; 
    sectorId?: number;
  };
  'demand-updated': { 
    demandId: number; 
    changes: any; 
    demanda?: any;
  };
  'demand-deleted': { 
    demandId: number;
  };
  'user-updated': { 
    userId: number; 
    user: any;
  };
  'cache-invalidated': { 
    cacheKeys: string[];
  };
}

// Singleton EventBus para comunicação global
class EventBusService {
  private listeners = new Map<EventType, Set<(data: any) => void>>();
  private static instance: EventBusService;

  static getInstance(): EventBusService {
    if (!EventBusService.instance) {
      EventBusService.instance = new EventBusService();
    }
    return EventBusService.instance;
  }

  /**
   * Emite um evento para todos os listeners
   */
  emit<T extends EventType>(type: T, data: EventData[T]): void {
    const listeners = this.listeners.get(type);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Erro ao processar evento ${type}:`, error);
        }
      });
    }
  }

  /**
   * Adiciona um listener para um tipo de evento
   */
  on<T extends EventType>(type: T, callback: (data: EventData[T]) => void): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    
    const listeners = this.listeners.get(type)!;
    listeners.add(callback);

    // Retorna função para remover o listener
    return () => {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.listeners.delete(type);
      }
    };
  }

  /**
   * Remove um listener específico
   */
  off<T extends EventType>(type: T, callback: (data: EventData[T]) => void): void {
    const listeners = this.listeners.get(type);
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.listeners.delete(type);
      }
    }
  }

  /**
   * Remove todos os listeners de um tipo de evento
   */
  removeAllListeners(type?: EventType): void {
    if (type) {
      this.listeners.delete(type);
    } else {
      this.listeners.clear();
    }
  }

  /**
   * Verifica se há listeners para um tipo de evento
   */
  hasListeners(type: EventType): boolean {
    const listeners = this.listeners.get(type);
    return !!listeners && listeners.size > 0;
  }

  /**
   * Retorna estatísticas do EventBus
   */
  getStats(): { totalTypes: number; totalListeners: number; types: EventType[] } {
    let totalListeners = 0;
    const types: EventType[] = [];

    this.listeners.forEach((listeners, type) => {
      totalListeners += listeners.size;
      types.push(type);
    });

    return {
      totalTypes: this.listeners.size,
      totalListeners,
      types
    };
  }
}

// Hook para usar o EventBus em componentes React
export const useEventBus = () => {
  const eventBus = useRef(EventBusService.getInstance());

  return eventBus.current;
};

// Hook para escutar eventos específicos
export const useEventListener = <T extends EventType>(
  type: T,
  callback: (data: EventData[T]) => void,
  deps: any[] = []
) => {
  const eventBus = useEventBus();

  useEffect(() => {
    const unsubscribe = eventBus.on(type, callback);
    return unsubscribe;
  }, [eventBus, type, ...deps]);
};

// Hook para emitir eventos
export const useEventEmitter = () => {
  const eventBus = useEventBus();

  return {
    emit: eventBus.emit.bind(eventBus),
    hasListeners: eventBus.hasListeners.bind(eventBus),
    getStats: eventBus.getStats.bind(eventBus)
  };
};

// Instância global para usar fora de componentes React
export const eventBus = EventBusService.getInstance();